import { useState } from 'react';
import { Paperclip, FileText, Download, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ClassificationBadge } from '@/components/custom/classification-badge';
import type { EmailAttachmentResponse } from '@vagusx/clients';
import type { ClassificationLabelMap } from '@/hooks/use-classification-label';
import { getEmailAttachmentFileUrl } from '@/hooks/use-emails';
import { deduplicateAttachments } from '@/lib/attachment-utils';

interface EmailAttachmentSectionProps {
  attachments: EmailAttachmentResponse[];
  labelMap: ClassificationLabelMap;
  onAttachmentClick: (attachmentId: string) => void;
}

function formatFileSize(bytes: number | null): string {
  if (bytes == null || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function triggerDownload(attachmentId: string, fileName: string): void {
  const url = getEmailAttachmentFileUrl(attachmentId);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  processing: 'Processing',
  extracted: 'Extracted',
  failed: 'Failed',
  skipped: 'Skipped',
};

function isAudioContentType(contentType: string | null | undefined): boolean {
  return (contentType ?? '').startsWith('audio/');
}

function AttachmentRow({
  attachment,
  labelMap,
  onClick,
}: {
  attachment: EmailAttachmentResponse;
  labelMap: ClassificationLabelMap;
  onClick: () => void;
}): React.ReactElement {
  const statusLabel = STATUS_LABELS[attachment.status] ?? attachment.status;
  const isAudio = isAudioContentType(attachment.contentType);
  const [showTranscript, setShowTranscript] = useState(false);

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent/50"
      >
        <FileText size={16} className="shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{attachment.fileName}</span>
            {attachment.fileSize != null && (
              <span className="text-[11px] text-muted-foreground">
                {formatFileSize(attachment.fileSize)}
              </span>
            )}
          </div>
          {(attachment.status !== 'pending' || attachment.labels.length > 0) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              {attachment.status !== 'pending' && (
                <Badge variant="outline" className="h-4 px-1.5 py-0 text-[10px]">
                  {statusLabel}
                </Badge>
              )}
              {attachment.labels.map((label) => (
                <ClassificationBadge
                  key={label}
                  classification={label}
                  labelMap={labelMap}
                  size="sm"
                />
              ))}
            </div>
          )}
        </div>
        <span
          role="button"
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation();
            triggerDownload(attachment.id, attachment.fileName);
          }}
          className="shrink-0 rounded p-1 hover:bg-accent"
          title="Download"
        >
          <Download size={14} className="text-muted-foreground" />
        </span>
      </button>

      {/* Audio player + transcript expander */}
      {isAudio && (
        <div className="border-t border-border px-3 py-2 space-y-2">
          <audio controls preload="none" crossOrigin="use-credentials" className="h-8 w-full">
            <source src={getEmailAttachmentFileUrl(attachment.id)} type={attachment.contentType} />
          </audio>
          {attachment.transcript && (
            <div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTranscript(!showTranscript);
                }}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <ChevronRight
                  size={12}
                  className={`transition-transform ${showTranscript ? 'rotate-90' : ''}`}
                />
                {showTranscript ? 'Hide transcript' : 'Show transcript'}
              </button>
              {showTranscript && (
                <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground leading-relaxed">
                  {attachment.transcript}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function EmailAttachmentSection({
  attachments,
  labelMap,
  onAttachmentClick,
}: EmailAttachmentSectionProps): React.ReactElement | null {
  // Filter out inline CID images, then deduplicate by fileName
  const displayAttachments = deduplicateAttachments(attachments.filter((att) => !att.contentId));

  if (displayAttachments.length === 0) return null;

  return (
    <div className="space-y-2 border-t border-border px-4 py-3">
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Paperclip size={14} />
        <span>
          {displayAttachments.length} attachment
          {displayAttachments.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="space-y-1.5">
        {displayAttachments.map((att) => (
          <AttachmentRow
            key={att.id}
            attachment={att}
            labelMap={labelMap}
            onClick={() => onAttachmentClick(att.id)}
          />
        ))}
      </div>
    </div>
  );
}
